import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Instagram, Filter, Check, MessageCircle, Heart } from 'lucide-react';
import api from '../utils/api';
import { addToCart } from '../utils/cart';
import { openWhatsAppInquiry } from '../utils/inquiry';
import { toast } from 'sonner';
import { normalizeImages } from '../utils/imageUtils';

const PAGE_SIZE = 12; // 4 columns × 3 rows

// ── Product card (mirrors CollectionsPage card, with Instagram link) ──────────
const ProductCard = ({ product, index }) => {
  const [imageIdx, setImageIdx] = useState(0);
  const images = normalizeImages(product.images);

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
    window.dispatchEvent(new Event('cartUpdated'));
    toast.success('Added to cart!');
  };

  const handleWhatsApp = (e) => {
    e.preventDefault();
    e.stopPropagation();
    openWhatsAppInquiry({
      productSku: product.sku,
      productCategory: product.category,
      productTitle: product.title,
    });
  };

  const nextImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setImageIdx((imageIdx + 1) % images.length);
  };

  const prevImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setImageIdx((imageIdx - 1 + images.length) % images.length);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: (index % PAGE_SIZE) * 0.04 }}
    >
      <Link to={`/product/${product.sku}`} className="product-card p-4 group block" data-testid={`home-product-card-${product.sku}`}>
        <div className="relative overflow-hidden mb-3 aspect-[3/4]">
          <img
            src={images[imageIdx]}
            alt={product.title}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => {
              e.target.src = 'https://images.pexels.com/photos/10317127/pexels-photo-10317127.jpeg';
            }}
          />

          {['3XL', '4XL', '5XL'].includes(product.subcategory) && (
            <span className="absolute top-2 left-2 px-2 py-0.5 text-xs font-bold bg-[#2A0000] text-white rounded z-10">
              {product.subcategory}
            </span>
          )}

          {product.instagram_video_url && (
            <a
              href={product.instagram_video_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              title="Watch on Instagram"
              className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center text-white shadow z-10 hover:scale-110 transition-transform"
              style={{ background: 'linear-gradient(90deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)' }}
              data-testid={`home-instagram-${product.sku}`}
            >
              <Instagram className="w-4 h-4" />
            </a>
          )}

          {images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 hover:bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <ChevronLeft className="w-4 h-4 text-[#2A0000]" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 hover:bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <ChevronRight className="w-4 h-4 text-[#2A0000]" />
              </button>
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1">
                {images.map((_, i) => (
                  <div key={i} className={`w-1.5 h-1.5 rounded-full ${i === imageIdx ? 'bg-white' : 'bg-white/50'}`} />
                ))}
              </div>
            </>
          )}
        </div>

        <h3 className="font-semibold text-[#2C2C2C] mb-1 line-clamp-1">{product.title}</h3>
        <p className="text-sm text-gray-600 mb-1 truncate">{product.sku}</p>
        <p className="text-sm text-gray-600 mb-4 line-clamp-1">Color: {product.color}</p>

        <div className="flex flex-col gap-2">
          <button
            onClick={handleWhatsApp}
            className="flex items-center justify-center gap-1.5 w-full bg-[#25D366] text-white py-2.5 rounded-md hover:bg-[#20BA5A] active:scale-[0.98] transition-all text-xs sm:text-sm font-semibold whitespace-nowrap"
          >
            <MessageCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
            <span className="sm:hidden">WhatsApp</span>
            <span className="hidden sm:inline">Inquire on WhatsApp</span>
          </button>
          <button
            onClick={handleAddToCart}
            className="flex items-center justify-center gap-1.5 w-full py-2.5 rounded-md border border-[#E5A93C] text-[#2A0000] hover:bg-[#E5A93C] hover:text-white active:scale-[0.98] transition-all text-xs sm:text-sm font-semibold whitespace-nowrap"
          >
            <Heart className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
            <span className="sm:hidden">Wishlist</span>
            <span className="hidden sm:inline">Add to Wishlist</span>
          </button>
        </div>
      </Link>
    </motion.div>
  );
};

// ── Category filter dropdown (top-right corner) ───────────────────────────────
const CategoryFilter = ({ categories, active, onSelect }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const activeLabel = active === 'all' ? 'All Categories' : active;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-4 py-2.5 rounded-sm border border-[#E5A93C]/40 bg-white text-sm font-medium text-[#2A0000] hover:border-[#E5A93C] transition-colors"
        data-testid="home-category-filter-button"
      >
        <Filter className="w-4 h-4 text-[#E5A93C]" />
        <span className="whitespace-nowrap">{activeLabel}</span>
        <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-100 rounded-lg shadow-xl z-30 py-2 max-h-80 overflow-y-auto">
          <button
            onClick={() => { onSelect('all'); setOpen(false); }}
            className={`w-full flex items-center justify-between px-4 py-2.5 text-sm text-left hover:bg-[#E5A93C]/10 transition-colors ${
              active === 'all' ? 'text-[#E5A93C] font-semibold' : 'text-[#2A0000]'
            }`}
          >
            All Categories
            {active === 'all' && <Check className="w-4 h-4" />}
          </button>
          {categories.map((cat) => (
            <button
              key={cat.slug}
              onClick={() => { onSelect(cat.name); setOpen(false); }}
              className={`w-full flex items-center justify-between px-4 py-2.5 text-sm text-left hover:bg-[#E5A93C]/10 transition-colors ${
                active === cat.name ? 'text-[#E5A93C] font-semibold' : 'text-[#2A0000]'
              }`}
            >
              {cat.name}
              {active === cat.name && <Check className="w-4 h-4" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Main section ──────────────────────────────────────────────────────────────
const AllProductsSection = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const sectionRef = useRef(null);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await api.get('/products?status=published&limit=100');
      setProducts(res.data || []);
    } catch (e) {
      console.error('Error fetching products:', e);
    }
    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const res = await api.get('/categories?visible=true');
      setCategories(res.data || []);
    } catch (e) {
      console.error('Error fetching categories:', e);
    }
  };

  const filteredProducts = useMemo(() => {
    if (activeCategory === 'all') return products;
    return products.filter((p) => p.category === activeCategory);
  }, [products, activeCategory]);

  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageProducts = filteredProducts.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleSelectCategory = (cat) => {
    setActiveCategory(cat);
    setPage(1);
  };

  const goToPage = (p) => {
    setPage(p);
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section ref={sectionRef} className="py-12 md:py-20 bg-white" data-testid="all-products-section">
      <div className="container mx-auto px-4 md:px-8">

        {/* Header row: title left, filter top-right corner */}
        <div className="flex items-center justify-between gap-4 mb-8 md:mb-10">
          <div>
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#E5A93C] mb-2">
              Explore The Collection
            </p>
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-[#2A0000] leading-none">
              Our Products
            </h2>
          </div>
          <CategoryFilter
            categories={categories}
            active={activeCategory}
            onSelect={handleSelectCategory}
          />
        </div>

        {loading ? (
          <div className="text-center py-20">
            <p className="text-gray-600">Loading products...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-600 text-lg mb-4">
              {activeCategory === 'all'
                ? 'No products available yet.'
                : `No products found in ${activeCategory}.`}
            </p>
            {activeCategory !== 'all' && (
              <button onClick={() => handleSelectCategory('all')} className="btn-primary px-6 py-2">
                View all products
              </button>
            )}
          </div>
        ) : (
          <>
            {/* 4 columns × 3 rows */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {pageProducts.map((product, index) => (
                <ProductCard key={product.sku} product={product} index={index} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10 md:mt-12 flex-wrap">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="flex items-center gap-1 px-3 py-2 rounded-sm border border-[#E5A93C]/30 text-sm text-[#2A0000] hover:bg-[#E5A93C] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-[#2A0000]"
                  data-testid="home-pagination-prev"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Prev
                </button>

                {Array.from({ length: totalPages }).map((_, i) => {
                  const p = i + 1;
                  return (
                    <button
                      key={p}
                      onClick={() => goToPage(p)}
                      className={`w-10 h-10 rounded-sm text-sm font-medium border transition-colors ${
                        p === currentPage
                          ? 'bg-[#2A0000] text-white border-[#2A0000]'
                          : 'bg-white text-[#2A0000] border-[#E5A93C]/30 hover:bg-[#E5A93C] hover:text-white hover:border-[#E5A93C]'
                      }`}
                      data-testid={`home-pagination-page-${p}`}
                    >
                      {p}
                    </button>
                  );
                })}

                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-1 px-3 py-2 rounded-sm border border-[#E5A93C]/30 text-sm text-[#2A0000] hover:bg-[#E5A93C] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-[#2A0000]"
                  data-testid="home-pagination-next"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default AllProductsSection;
